import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DataTable from '../../components/common/DataTable';

const ISFList = () => {
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [isfTypeFilter, setIsfTypeFilter] = useState('');

  useEffect(() => { fetchFilings(); }, []);

  const fetchFilings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/isf', { headers: { Authorization: `Bearer ${token}` } });
      setFilings(res.data);
    } catch (err) {
      console.error('Failed to fetch ISF filings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ISF filing?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/isf/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchFilings();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Draft: '#6c757d',
      Submitted: '#0d6efd',
      Accepted: '#198754',
      Matched: '#198754',
      Rejected: '#dc3545',
      Deleted: '#dc3545',
      'In Progress': '#fd7e14',
      Transmitted: '#0dcaf0',
    };
    const bg = colors[status] || '#6c757d';
    return (
      <span style={{
        background: bg, color: '#fff', padding: '2px 8px',
        borderRadius: '4px', fontSize: '11px', fontWeight: 600,
      }}>
        {status || 'Draft'}
      </span>
    );
  };

  const getBillOnFileBadge = (billOnFile) => {
    if (!billOnFile) return '';
    const color = billOnFile === 'Yes' ? '#198754' : billOnFile === 'No' ? '#dc3545' : '#6c757d';
    return (
      <span style={{
        background: color, color: '#fff', padding: '2px 6px',
        borderRadius: '4px', fontSize: '10px',
      }}>
        {billOnFile === 'Yes' ? 'Matched' : 'Not Matched'}
      </span>
    );
  };

  const filteredFilings = filings.filter(f => {
    if (statusFilter && f.status !== statusFilter) return false;
    if (isfTypeFilter && f.isf_type !== isfTypeFilter) return false;
    return true;
  });

  const columns = [
    {
      key: 'status', label: 'Status', width: '100px',
      render: (val, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {getStatusBadge(val)}
          {row.bill_on_file && getBillOnFileBadge(row.bill_on_file)}
        </div>
      ),
    },
    { key: 'file_no', label: 'File No.', width: '130px', sortable: true },
    { key: 'ams_no', label: 'AMS No.', width: '160px', sortable: true },
    { key: 'isf_no', label: 'ISF No.', width: '160px', sortable: true },
    { key: 'hbl_no', label: 'HB/L No.', width: '140px', sortable: true },
    { key: 'consignee', label: 'Consignee', width: '180px', sortable: true },
    { key: 'shipper', label: 'Shipper', width: '180px', sortable: true },
    { key: 'etd', label: 'ETD', width: '100px', sortable: true },
    { key: 'eta', label: 'ETA', width: '100px', sortable: true },
    {
      key: 'first_submit_date', label: 'First Submit', width: '130px', sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString() : '',
    },
    {
      key: 'latest_submit_date', label: 'Latest Submit', width: '130px', sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString() : '',
    },
    {
      key: 'cbp_accept_date', label: 'CBP Accept', width: '120px', sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString() : '',
    },
    {
      key: 'first_matched_date', label: 'First Matched', width: '130px', sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString() : '',
    },
    { key: 'isf_type', label: 'ISF Type', width: '80px' },
    {
      key: 'linked_file_no', label: 'Linked', width: '120px',
      render: (val, row) => val ? (
        <span
          style={{ color: '#0d6efd', cursor: 'pointer', fontSize: '12px' }}
          onClick={(e) => {
            e.stopPropagation();
            const path = row.linked_shipment_type === 'ocean_import' ? '/ocean-import' : '/truck';
            navigate(`${path}/${row.linked_shipment_id}`);
          }}
        >
          {val}
        </span>
      ) : '',
    },
  ];

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>ISF Filings</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
          >
            <option value="">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Accepted">Accepted</option>
            <option value="Matched">Matched</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select
            value={isfTypeFilter}
            onChange={(e) => setIsfTypeFilter(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
          >
            <option value="">All Types</option>
            <option value="ISF-10">ISF-10</option>
            <option value="ISF-5">ISF-5</option>
          </select>
          <button
            onClick={() => navigate('/isf/new')}
            style={{
              background: '#0d6efd', color: '#fff', border: 'none', padding: '8px 16px',
              borderRadius: '4px', cursor: 'pointer', fontWeight: 600,
            }}
          >
            + New ISF
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredFilings}
        onRowClick={(row) => navigate(`/isf/${row.id}`)}
        onDelete={handleDelete}
        emptyMessage="No ISF filings found"
      />
    </div>
  );
};

export default ISFList;
